import { Flex, Box, TableContainer, Table, Thead, Tr, Th, Tbody } from '@chakra-ui/react';
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';

import ArrowLeftTable from 'components/Icons/ArrowLeftTable';
import ArrowRightTable from 'components/Icons/ArrowRightTable';
import { MONTH_NAMES } from 'projectConstants';

import DashboardTableRow from '../../table/DashboardTableRow';

export interface ExportTableProperties {
  tableData: Array<any> | null;
  expandkey: string;
  timeGranularity: string;
  tableHeaderData: Array<any> | null;
  isDashboardLayout: boolean;
  output: any;
  isDimensionFilter: Array<any>;
}
const ExportTable = ({
  tableData,
  expandkey,
  timeGranularity,
  tableHeaderData,
  isDashboardLayout,
}: ExportTableProperties) => {
  const parentReference = useRef() as React.MutableRefObject<HTMLDivElement>;
  const scrollReference = useRef<HTMLDivElement | null>(null);
  const [actualTableHeaders, setActualTableHeaders] = useState<any>(null);
  const [actualTableData, setActualTableData] = useState<Array<any>>([]);
  const [height, setHeight] = useState(0);
  const startDateObject = new Date('2024-05-08');
  const appliedFilters: any = [];
  const yearFormat = `FY${startDateObject.getFullYear().toString().slice(-2)}`;
  const quarterFormat = `Q${Math.ceil((startDateObject.getMonth() + 1) / 3)}-${String(
    yearFormat,
  ).slice(-2)}`;

  const monthFormat = `${MONTH_NAMES[startDateObject.getMonth()]}-${String(yearFormat).slice(-2)}`;

  const targetDate = () => {
    if (timeGranularity === 'M') {
      return monthFormat;
    }
    if (timeGranularity === 'Q') {
      return quarterFormat;
    }
    return yearFormat;
  };

  const dimensionFilters = (payload: any) => {
    const dimensionTableData = payload || [];
    if (
      appliedFilters?.length > 0 &&
      dimensionTableData?.length > 0 &&
      dimensionTableData?.[0]?.dims?.[0]
    ) {
      const hasDims = dimensionTableData[0]?.dims[0]?.Time;
      const dimensionIndex = appliedFilters?.findIndex((data: any) => {
        const object = Object.keys(data);
        return object[0] === hasDims;
      });
      const results = dimensionTableData?.map((data: any) => {
        if (data?.dims && data?.dims?.length && hasDims && appliedFilters[dimensionIndex]) {
          const filteredData = data?.dim_df?.filter((item: any) => {
            return appliedFilters[dimensionIndex][hasDims]?.includes(item?.Time?.trim());
          });
          return { ...data, dim_df: filteredData };
        }
        return data;
      });
      const sortedData = results?.sort((a: any, b: any) => {
        const positionA = a?.indicator?.position ?? 0;
        const positionB = b?.indicator?.position ?? 0;
        return positionA - positionB;
      });
      setActualTableData(sortedData);
    } else {
      const sortedData = dimensionTableData?.sort((a: any, b: any) => {
        const positionA = a?.indicator?.position ?? 0;
        const positionB = b?.indicator?.position ?? 0;
        return positionA - positionB;
      });
      setActualTableData(sortedData);
    }
  };

  useLayoutEffect(() => {
    if (parentReference?.current?.clientHeight) {
      setHeight(parentReference?.current?.clientHeight);
    }
  }, [parentReference?.current?.clientHeight]);

  useEffect(() => {
    if (tableHeaderData) {
      const actualDate = targetDate();
      const actualTableHeaderNew = [];
      let afterFlag = false;
      // setDataHadActualValue(false);
      for (let index = tableHeaderData.length - 1; index >= 0; index -= 1) {
        const item = JSON.parse(JSON.stringify(tableHeaderData[index]));
        if (item.Time === `${actualDate}`) {
          afterFlag = true;
          // setDataHadActualValue(true);
        }
        item.isActualValue = afterFlag;
        actualTableHeaderNew.push(item);
      }
      setActualTableHeaders(actualTableHeaderNew.reverse());
    }
  }, [timeGranularity, tableHeaderData]);

  useEffect(() => {
    if (tableData) {
      const actualTableDataNew = [];
      const actualDate = targetDate();
      // eslint-disable-next-line unicorn/no-for-loop
      for (let index = 0; index < tableData.length; index += 1) {
        const item = JSON.parse(JSON.stringify(tableData[index]));
        const totalValues = [];
        let afterFlag = false;
        for (let indexOne = item.total.length - 1; indexOne >= 0; indexOne -= 1) {
          const itemValue = JSON.parse(JSON.stringify(item.total[indexOne]));
          if (itemValue.Time === `${actualDate}`) {
            afterFlag = true;
          }
          itemValue.isActualValue = afterFlag;
          totalValues.push(itemValue);
        }
        item.total = totalValues.reverse();
        item.dim_df = item.dim_df?.map((dimValue: any) => {
          const dimValues = [];
          let flagNew = false;

          if (item?.dim_df) {
            for (let indexTwo = item.dim_df.length - 1; indexTwo >= 0; indexTwo -= 1) {
              const itemValue = JSON.parse(JSON.stringify(item.dim_df[indexTwo]));
              if (itemValue.Time === `${actualDate}`) {
                flagNew = true;
              }
              itemValue.isActualValue = flagNew;
              dimValues.push(itemValue);
            }
          }

          item.dim_df = dimValues.reverse();
          return dimValue;
        });
        actualTableDataNew.push(item);
      }
      dimensionFilters(actualTableDataNew);
    }
  }, [timeGranularity, tableData]);

  useEffect(() => {
    if (scrollReference.current && actualTableHeaders) {
      const scrollContainer = scrollReference.current;
      const targetIndex = actualTableHeaders.findIndex((header: any) => !header.isActualValue) - 1;
      if (targetIndex !== -1) {
        const targetElement = scrollContainer.querySelector(
          `th[data-index="${targetIndex}"]`,
        ) as HTMLElement;
        if (targetElement) {
          const { offsetLeft, offsetTop } = targetElement;
          scrollContainer.scrollTo({
            top: offsetTop - 250,
            left: offsetLeft - 250,
            behavior: 'smooth',
          });
        }
      }
    }
  }, [actualTableHeaders]);

  return (
    <Flex
      borderRadius='15px'
      flexDirection={'column'}
      gap={1}
      height={'100%'}
      ref={parentReference}
    >
      <Box px={4}>
        {actualTableHeaders && height && (
          <Box
            borderRadius='10px'
            overflowY='auto'
            overflowX='auto'
            // The dashboard reserves 50px above the table for its M/Q/Y
            // granularity toggle. The export doesn't render that control, so
            // the reservation just left a blank strip in the card and pushed
            // the last rows out of view. Only the horizontal scrollbar needs
            // room here.
            h={`${height ? height - 16 : 0}px`}
            className='scroll'
          >
            <TableContainer
              ref={scrollReference}
              overflowY='auto'
              overflowX='auto'
              mb={{ base: 4.75, md: 0 }}
              mt={{ base: 0, md: 0 }}
              borderRadius='10px'
              className='table_scroll indicator-table dashboard-drag-header'
              cursor='grab'
            >
              <Table variant='base'>
                <Thead height={'9'} zIndex={2}>
                  <Tr>
                    <Th
                      textAlign={'left'}
                      fontFamily={'regular_roboto'}
                      fontSize={'xs'}
                      fontWeight={500}
                      textTransform='none'
                    >
                      {''}
                    </Th>
                    {actualTableHeaders?.map(
                      (date: any, index: number) =>
                        date.isShow && (
                          <Th
                            data-index={index}
                            background={date?.isActualValue ? '#4AB7BF' : ''}
                            fontFamily={'regular_roboto'}
                            fontSize={'xs'}
                            fontWeight={500}
                            textTransform='none'
                            style={{
                              cursor: date.isShowExpandable ? 'pointer' : 'default',
                              paddingRight: '1rem',
                            }}
                            key={index}
                            justifyContent='space-around'
                            textAlign={'start'}
                          >
                            {date.isShowExpandable}
                            {date.isShowExpandable &&
                              (expandkey === date.Time ? <ArrowRightTable /> : <ArrowLeftTable />)}
                            {date.Time}
                          </Th>
                        ),
                    )}
                  </Tr>
                </Thead>

                {actualTableData && (
                  <Tbody>
                    {actualTableData?.map((row: any, index: number) => (
                      <React.Fragment key={index}>
                        <DashboardTableRow
                          isDashboardLayout={isDashboardLayout}
                          key={row.id}
                          row={row}
                          expandkey={expandkey}
                        />
                      </React.Fragment>
                    ))}
                  </Tbody>
                )}
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>
    </Flex>
  );
};
export default ExportTable;
